import { httpError } from './errors.js'

/**
 * Resolution rules: which copy of a duplicate group is the one worth keeping.
 *
 * Finding duplicates is the easy half. The half that decides whether the export
 * is usable is survivorship — of the three rows that share an email, which one
 * is *the* record? "The first one in the file" is only ever right by accident,
 * so this lets the rules be stated against the file's own columns:
 *
 *   1. newest        Last Modified
 *   2. one of these  Status: won > qualified > lost
 *   3. has any value Phone
 *   4. still tied?   first in the file
 *
 * Rules are tried in order and the first one that separates two rows decides;
 * a rule that cannot separate them falls through to the next. That makes the
 * order meaningful and every rule cheap — no sorting, no second pass.
 *
 * One principle throughout: **a blank never wins**. "Shortest value" picking the
 * row with no phone number at all would be technically correct and useless, so
 * an empty cell (and a non-number where a number is asked for, and a value that
 * is not on a preference list) scores `null`, which loses to everything except
 * another `null`.
 */

/** Value that loses to every real score. Not `-Infinity`: "smallest" reverses. */
const LOSES = null

/**
 * The rule catalogue. Mirrors OPERATORS in filters.js: the client renders from
 * this, so client and server can never disagree on what a rule means.
 *
 * `column` — needs a column to read.  `values` — needs a priority list.
 * `dates`  — offers the day-first toggle.  `cased` — offers match-case.
 */
export const PREFERENCES = [
  {
    id: 'newest',
    label: 'newest date',
    group: 'Dates',
    column: true,
    dates: true,
    hint: 'Latest date wins. Reads 2026-02-11, 11/02/2026, 11 Feb 2026, 20260211 and epoch numbers.',
  },
  {
    id: 'oldest',
    label: 'oldest date',
    group: 'Dates',
    column: true,
    dates: true,
    hint: 'Earliest date wins. Unreadable dates and blanks never win.',
  },
  {
    id: 'max',
    label: 'largest number',
    group: 'Numbers',
    column: true,
    hint: 'Highest number wins. A cell that is not a number never wins.',
  },
  {
    id: 'min',
    label: 'smallest number',
    group: 'Numbers',
    column: true,
    hint: 'Lowest number wins.',
  },
  {
    id: 'values',
    label: 'one of these values',
    group: 'Values',
    column: true,
    values: true,
    cased: true,
    hint: 'Your list, best first: the row with the highest-ranked value wins. Anything not on the list never wins.',
  },
  {
    id: 'non_empty',
    label: 'has any value',
    group: 'Values',
    column: true,
    hint: 'A filled cell beats a blank one — and nothing more, so ties fall to the next rule.',
  },
  {
    id: 'longest',
    label: 'longest value',
    group: 'Text',
    column: true,
    hint: 'Most characters wins — usually the fuller-looking value.',
  },
  {
    id: 'shortest',
    label: 'shortest value',
    group: 'Text',
    column: true,
    hint: 'Fewest characters wins. Blanks still lose.',
  },
  {
    id: 'first_alpha',
    label: 'first A→Z',
    group: 'Text',
    column: true,
    cased: true,
    hint: 'Alphabetically first wins.',
  },
  {
    id: 'last_alpha',
    label: 'last A→Z',
    group: 'Text',
    column: true,
    cased: true,
    hint: 'Alphabetically last wins.',
  },
  {
    id: 'most_filled',
    label: 'most fields filled',
    group: 'Whole row',
    column: false,
    hint: 'The most complete row wins — the one with the fewest empty cells, across every column.',
  },
]

/** What breaks a tie no rule could. `first` is the file's own order. */
export const TIEBREAKS = [
  { id: 'first', label: 'first in the file' },
  { id: 'last', label: 'last in the file' },
]

export const MAX_RESOLVE_RULES = 12

const PREF_BY_ID = new Map(PREFERENCES.map((p) => [p.id, p]))
const NUMERIC = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/

/* ------------------------------------------------------------------- dates */

const MONTHS = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
}

const ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*(Z|[+-]\d{2}:?\d{2})?)?$/
const YMD = /^(\d{4})[/.](\d{1,2})[/.](\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
const DMY = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
const DAY_MONTH = /^(\d{1,2})[ -]([A-Za-z]{3,9})\.?[ -](\d{4})(?:[T ,]\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
const MONTH_DAY = /^([A-Za-z]{3,9})\.?[ ](\d{1,2}),?[ ](\d{4})(?:[T ,]\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
const COMPACT = /^(\d{4})(\d{2})(\d{2})$/
const EPOCH = /^\d{10}(\d{3})?$/

function utc(y, mo, d, hh = 0, mi = 0, ss = 0) {
  if (!(mo >= 1 && mo <= 12) || !(d >= 1 && d <= 31) || hh > 23 || mi > 59 || ss > 60) return LOSES
  const t = Date.UTC(y, mo - 1, d, hh, mi, ss)
  return Number.isNaN(t) ? LOSES : t
}

/**
 * Best-effort epoch milliseconds for a date written the way spreadsheets write
 * them. Deliberately narrow: everything without an explicit zone is read as UTC,
 * so two rows always compare the same way whatever machine the server is on.
 *
 * `01/02/2026` is genuinely ambiguous, so `dayFirst` says which way to read it —
 * except when one of the numbers is over 12, which settles it on its own.
 *
 * @returns {number|null} null when the value is not a date this understands
 */
export function toTimestamp(raw, dayFirst = false) {
  const s = String(raw ?? '').trim()
  if (s === '') return LOSES

  let m = ISO.exec(s) || YMD.exec(s)
  if (m) {
    // An explicit offset is the one case the platform parser gets right for free.
    if (m[7]) {
      const t = Date.parse(s)
      return Number.isNaN(t) ? LOSES : t
    }
    return utc(+m[1], +m[2], +m[3], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0))
  }

  if ((m = DMY.exec(s))) {
    let day = +m[1]
    let mon = +m[2]
    if (mon > 12) [day, mon] = [mon, day] // 13/02: only one reading is a date
    else if (!dayFirst && day <= 12) [day, mon] = [mon, day]
    return utc(+m[3], mon, day, +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0))
  }

  if ((m = DAY_MONTH.exec(s))) {
    const mon = MONTHS[m[2].slice(0, 3).toLowerCase()]
    return mon ? utc(+m[3], mon, +m[1], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0)) : LOSES
  }

  if ((m = MONTH_DAY.exec(s))) {
    const mon = MONTHS[m[1].slice(0, 3).toLowerCase()]
    return mon ? utc(+m[3], mon, +m[2], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0)) : LOSES
  }

  if ((m = COMPACT.exec(s))) return utc(+m[1], +m[2], +m[3])
  // Epoch seconds or milliseconds — 10 digits is 2001-2286, 13 is the same in ms.
  if (EPOCH.test(s)) return s.length === 13 ? +s : +s * 1000
  return LOSES
}

/* ------------------------------------------------------------ the rule spec */

/**
 * Validate and canonicalize what the client sent, without needing a file: views
 * are saved and cache keys are built long before a dataset is in hand.
 *
 * @returns {null | {rules: object[], tiebreak: 'first'|'last', merge?: true}} null
 *   when the spec says nothing the default does not already do — no rules, keep
 *   the first copy in the file — so an untouched panel costs no cache key and no
 *   rescan.
 */
export function normalizeResolution(input) {
  if (!input) return null
  if (typeof input !== 'object' || Array.isArray(input)) throw httpError(400, 'resolution must be an object.')

  const tiebreak = input.tiebreak === 'last' ? 'last' : 'first'
  const merge = !!input.merge
  const raw = Array.isArray(input.rules) ? input.rules : []
  if (raw.length > MAX_RESOLVE_RULES) {
    throw httpError(400, `A resolution can have at most ${MAX_RESOLVE_RULES} rules.`)
  }

  const rules = []
  for (const rule of raw) {
    const pref = PREF_BY_ID.get(rule?.prefer)
    if (!pref) throw httpError(400, `Unknown resolution rule: ${rule?.prefer}`)
    const out = { prefer: pref.id }

    if (pref.column) {
      const column = typeof rule.column === 'string' ? rule.column.trim() : ''
      if (column === '') throw httpError(400, `Pick a column for “${pref.label}”.`)
      out.column = column
    }
    if (pref.values) {
      // Order is the whole point of this rule, so it is preserved, not sorted.
      // Blanks are dropped: a blank never wins, so "prefer blank" cannot be said.
      const values = (Array.isArray(rule.values) ? rule.values : [])
        .map((v) => String(v ?? '').trim())
        .filter((v) => v !== '')
      if (values.length === 0) throw httpError(400, `List at least one value for “${pref.label}” on ${out.column}.`)
      out.values = values
    }
    if (pref.dates && rule.dayFirst) out.dayFirst = true
    if (pref.cased && rule.caseSensitive) out.caseSensitive = true
    // A field-scoped rule decides one column's value rather than ranking whole
    // rows. Only meaningful with `merge`, and only for a rule that reads a column.
    if (rule.scope === 'field' && pref.column) out.scope = 'field'
    rules.push(out)
  }

  if (rules.length === 0 && tiebreak === 'first' && !merge) return null
  return merge ? { rules, tiebreak, merge: true } : { rules, tiebreak }
}

/** Stable cache-key fragment. Canonical already, so stringifying is enough. */
export function resolutionKey(resolution) {
  const spec = normalizeResolution(resolution)
  return spec ? JSON.stringify(spec) : ''
}

/** One line for a saved view's summary. */
export function describeResolution(resolution) {
  const spec = normalizeResolution(resolution)
  if (!spec) return null
  const tie = spec.tiebreak === 'last' ? 'last copy' : 'first copy'
  const how = spec.merge ? 'merged' : 'resolved'
  if (spec.rules.length === 0) return spec.merge ? 'merged into one record' : `keeping the ${tie}`
  return `${how} by ${spec.rules.length} rule${spec.rules.length === 1 ? '' : 's'}`
}

/* --------------------------------------------------------------- the runtime */

function columnIndex(headers, name) {
  const i = headers.indexOf(name)
  if (i < 0) throw httpError(400, `Unknown column: ${name}`)
  return i
}

/**
 * A row of already-decoded values, in the shape the rules read. Scans hand the
 * rules a RecordCursor (lazy, no strings built); merging hands them this, because
 * by then it has read the values anyway.
 */
export function rowView(values) {
  return {
    get: (i) => values[i] ?? '',
    filled: () => {
      let n = 0
      for (let i = 0; i < values.length; i++) if (values[i] !== '') n++
      return n
    },
  }
}

/** Which of `a` and `b` wins, for a rule scoring in direction `dir`. */
function cmpScore(a, b, dir) {
  if (a === LOSES) return b === LOSES ? 0 : -1
  if (b === LOSES) return 1
  if (a === b) return 0
  return a > b ? dir : -dir
}

/**
 * Compile one rule to `{value, dir}`: a score for a row, and which direction
 * wins. Scores are compared with `>` only, so a rule may return numbers or
 * strings as long as it always returns the same kind — or `null` to lose.
 */
function compileScorer(rule, headers) {
  const col = rule.column === undefined ? -1 : columnIndex(headers, rule.column)
  const cs = !!rule.caseSensitive
  const norm = cs ? (s) => s : (s) => s.toLowerCase()

  switch (rule.prefer) {
    case 'newest':
    case 'oldest': {
      const dayFirst = !!rule.dayFirst
      return { value: (c) => toTimestamp(c.get(col), dayFirst), dir: rule.prefer === 'newest' ? 1 : -1 }
    }
    case 'max':
    case 'min':
      return {
        value: (c) => {
          const v = c.get(col)
          return NUMERIC.test(v) ? Number(v) : LOSES
        },
        dir: rule.prefer === 'max' ? 1 : -1,
      }
    case 'non_empty':
      // Every filled cell scores the same: this rule only ever says "not blank".
      return { value: (c) => (c.get(col) === '' ? LOSES : 1), dir: 1 }
    case 'longest':
    case 'shortest':
      return {
        value: (c) => {
          const v = c.get(col)
          return v === '' ? LOSES : v.length
        },
        dir: rule.prefer === 'longest' ? 1 : -1,
      }
    case 'first_alpha':
    case 'last_alpha':
      return {
        value: (c) => {
          const v = c.get(col)
          return v === '' ? LOSES : norm(v)
        },
        dir: rule.prefer === 'last_alpha' ? 1 : -1,
      }
    case 'values': {
      // Rank in the list, so earlier is better; unlisted values are not ranked.
      const rank = new Map()
      rule.values.forEach((v, i) => {
        const key = norm(v)
        if (!rank.has(key)) rank.set(key, i)
      })
      return {
        value: (c) => {
          const r = rank.get(norm(c.get(col)))
          return r === undefined ? LOSES : r
        },
        dir: -1,
      }
    }
    case 'most_filled':
      // Off the record's byte offsets during a scan; counted from the decoded
      // values when merging compares rows it has already read.
      return { value: (r) => r.filled(), dir: 1 }
    default:
      throw httpError(400, `Unsupported resolution rule: ${rule.prefer}`)
  }
}

/** The rule plus the column it reads, which a field-scoped rule needs. */
function compileRule(rule, headers) {
  return {
    ...compileScorer(rule, headers),
    col: rule.column === undefined ? -1 : columnIndex(headers, rule.column),
  }
}

/**
 * Build the machinery for a resolution spec. Two jobs come out of one rule list:
 *
 *   ranking   the row-scoped rules put a group's copies in priority order. The
 *             scan uses this as a running champion — score the row, compare it to
 *             the champion rule by rule, replace it if it wins — so picking the
 *             surviving row costs no extra pass.
 *
 *   merging   with `merge` on, the same order becomes a waterfall *per field*:
 *             every column takes its value from the highest-priority copy that
 *             has one, so a record can draw its name from one copy and its phone
 *             from another. A field-scoped rule overrides the waterfall for its
 *             own column — "the status is whatever the best status among these
 *             copies is", whatever the top copy happens to say.
 *
 * @returns {null | object} null when the spec is the default (keep the first
 *   copy), which the caller already implements by keeping the first row it sees.
 */
export function compileResolution(resolution, headers) {
  const spec = normalizeResolution(resolution)
  if (!spec) return null

  const rank = [] // row-scoped: order the copies
  const fields = new Map() // column index -> the rule that decides that column
  for (const rule of spec.rules) {
    const compiled = compileRule(rule, headers)
    // A field rule with no merge to apply to is inert rather than an error: the
    // user may be toggling merge off to compare, and the rule is still their setup.
    if (rule.scope === 'field' && spec.merge) fields.set(compiled.col, compiled)
    else if (rule.scope !== 'field') rank.push(compiled)
  }

  // Reused across rows: scoring every candidate row of a huge file must not allocate.
  const scratch = new Array(rank.length)
  const tiebreakLast = spec.tiebreak === 'last'

  const compiled = {
    count: rank.length,
    merge: !!spec.merge,
    fieldColumns: [...fields.keys()],
    tiebreakLast,

    /** Scores for this row, one per ranking rule. Reused — copy it to keep it. */
    score(row) {
      for (let i = 0; i < rank.length; i++) scratch[i] = rank[i].value(row)
      return scratch
    },

    /** > 0 when `a` beats `b` on ranking rule `r`, < 0 when it loses, 0 when tied. */
    compare(a, b, r) {
      return cmpScore(a, b, rank[r].dir)
    },

    /**
     * A group's copies in priority order, best first. Small arrays — a group is
     * two or three rows — so this sorts rather than streams.
     *
     * @param rows row views, in file order
     * @returns {number[]} indices into `rows`
     */
    order(rows) {
      const idx = rows.map((_, i) => i)
      if (rank.length === 0) return tiebreakLast ? idx.reverse() : idx
      const scored = rows.map((row) => [...compiled.score(row)])
      idx.sort((a, b) => {
        for (let r = 0; r < rank.length; r++) {
          const d = compiled.compare(scored[a][r], scored[b][r], r)
          if (d !== 0) return -d // the winner of the rule comes first
        }
        return tiebreakLast ? b - a : a - b
      })
      return idx
    },

    /**
     * One clean record out of a group's copies.
     *
     * @param rows row views, in file order
     * @param columnCount how many columns the output has
     * @returns {{values: string[], donor: Int32Array, base: number}} the assembled
     *   row, which copy supplied each column (-1 where every copy was blank), and
     *   which copy is the base record
     */
    assemble(rows, columnCount) {
      const order = compiled.order(rows)
      const values = new Array(columnCount).fill('')
      const donor = new Int32Array(columnCount).fill(-1)

      for (let c = 0; c < columnCount; c++) {
        // A rule bound to this column answers for it outright.
        const rule = fields.get(c)
        if (rule) {
          const k = pickBy(rows, order, rule)
          if (k >= 0) {
            const v = rows[k].get(c)
            if (v !== '') {
              values[c] = v
              donor[c] = k
              continue
            }
          }
          // No copy satisfied the rule (no listed status anywhere, say), so this
          // column falls back to the waterfall rather than coming out blank.
        }
        for (let i = 0; i < order.length; i++) {
          const v = rows[order[i]].get(c)
          if (v !== '') {
            values[c] = v
            donor[c] = order[i]
            break
          }
        }
      }
      return { values, donor, base: order.length > 0 ? order[0] : -1 }
    },
  }
  return compiled
}

/**
 * The copy a single rule points at, or -1 when no copy satisfies it. Ties keep
 * the higher-priority copy, since `order` is walked in priority order and a
 * later copy has to be strictly better to take over.
 */
function pickBy(rows, order, rule) {
  let best = -1
  let bestScore = LOSES
  for (let i = 0; i < order.length; i++) {
    const k = order[i]
    const score = rule.value(rows[k])
    if (score === LOSES) continue
    if (best < 0 || cmpScore(score, bestScore, rule.dir) > 0) {
      best = k
      bestScore = score
    }
  }
  return best
}
